/**
 * "How this product ranks" — its position on every catalog leaderboard.
 *
 * ORDERING
 * --------
 * The criteria you picked on the leaderboard come FIRST, in the order you
 * picked them, because that is the question you were asking when you opened
 * this product. Everything else follows underneath, so a product can still
 * surprise you — a cheap-per-100g pick that also happens to be top 5% for
 * protein is worth seeing.
 *
 * A rank is meaningless without the size of the field, so every row states both
 * ("#312 of 46,574"). Boards the product cannot be ranked on show the reason
 * instead of a position — appearing last and being unrankable are different
 * facts, and the app never conflates them.
 */

import { useEffect, useState } from 'react';
import { ApiError, getProductRanks, type BoardRank } from '@/api/client';

/** Top 10% is genuinely notable; below that, just state the position. */
const NOTABLE_PERCENTILE = 10;

interface RankPanelProps {
  productId: string;
  /** Criteria chosen on the leaderboard, in selection order. */
  selectedBoardIds?: string[];
}

function RankRow({ rank: r, emphasis }: { rank: BoardRank; emphasis: boolean }): React.JSX.Element {
  const notable = r.percentile !== null && r.percentile <= NOTABLE_PERCENTILE;
  return (
    <div className={`rank-row${emphasis ? ' is-chosen' : ''}`} title={r.formula}>
      <span className="rank-label">{r.label}</span>
      <span className="score-bar">
        {/* Bar fills toward the top of the field, so longer = better. */}
        <span
          className="score-bar-fill"
          style={{
            width: `${Math.max(2, 100 - (r.percentile ?? 100))}%`,
            background: notable ? 'var(--good)' : 'var(--accent)',
          }}
        />
      </span>
      <span className="rank-place">
        <strong style={{ color: notable ? 'var(--good)' : 'var(--text-primary)' }}>
          #{r.rank!.toLocaleString()}
        </strong>
        <span className="rank-of">of {r.eligible.toLocaleString()}</span>
      </span>
    </div>
  );
}

export function RankPanel({
  productId,
  selectedBoardIds = [],
}: RankPanelProps): React.JSX.Element | null {
  const [ranks, setRanks] = useState<BoardRank[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getProductRanks(productId, controller.signal)
      .then((r) => setRanks(r.data.ranks))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not load rankings.');
      });
    return () => controller.abort();
  }, [productId]);

  if (error !== null) return null;
  if (ranks === null) {
    return (
      <section className="panel">
        <h2 className="panel-title">How this ranks</h2>
        <div className="spinner-row">Computing rankings…</div>
      </section>
    );
  }

  const ranked = ranks.filter((r) => r.rank !== null);
  const unranked = ranks.filter((r) => r.rank === null);

  // Chosen criteria keep the user's own order; the rest keep the board order.
  const chosen = selectedBoardIds
    .map((id) => ranked.find((r) => r.boardId === id))
    .filter((r): r is BoardRank => r !== undefined);
  const chosenIds = new Set(chosen.map((r) => r.boardId));
  const rest = ranked.filter((r) => !chosenIds.has(r.boardId));

  // A chosen criterion this product cannot be ranked on is a direct answer to
  // the user's question, so it is stated here rather than hidden in the
  // disclosure at the bottom.
  const chosenUnranked = unranked.filter((r) => selectedBoardIds.includes(r.boardId));
  const otherUnranked = unranked.filter((r) => !selectedBoardIds.includes(r.boardId));

  const highlights = ranked.filter(
    (r) => r.percentile !== null && r.percentile <= NOTABLE_PERCENTILE,
  );

  return (
    <section className="panel" aria-labelledby="rank-heading">
      <h2 className="panel-title" id="rank-heading">
        How this ranks
      </h2>

      {ranked.length === 0 && chosenUnranked.length === 0 ? (
        <p className="na" style={{ fontSize: '0.85rem', margin: 0 }}>
          This product cannot be ranked on any board yet.
        </p>
      ) : (
        <>
          {(chosen.length > 0 || chosenUnranked.length > 0) && (
            <div className="rank-group">
              <h3 className="rank-group-title">
                Your criteria
                <span className="rank-group-count">{selectedBoardIds.length}</span>
              </h3>
              <div className="rank-rows">
                {chosen.map((r) => (
                  <RankRow key={r.boardId} rank={r} emphasis />
                ))}
                {chosenUnranked.map((r) => (
                  <div className="rank-row is-chosen is-unranked" key={r.boardId}>
                    <span className="rank-label">{r.label}</span>
                    <span className="rank-none">{r.unavailableReason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rest.length > 0 && (
            <div className="rank-group">
              <h3 className="rank-group-title rank-group-title--muted">Everything else</h3>
              <div className="rank-rows">
                {rest.map((r) => (
                  <RankRow key={r.boardId} rank={r} emphasis={false} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {highlights.length > 0 && (
        <p style={{ fontSize: '0.8rem', margin: '0.6rem 0 0' }}>
          {highlights.map((r) => `Top ${r.percentile}% for ${r.label.toLowerCase()}`).join(' · ')}.
        </p>
      )}

      {otherUnranked.length > 0 && (
        <details style={{ marginTop: '0.6rem' }}>
          <summary style={{ fontSize: '0.78rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
            {otherUnranked.length} board{otherUnranked.length === 1 ? '' : 's'} cannot rank this
            product
          </summary>
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
            {otherUnranked.map((r) => (
              <li key={r.boardId} style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                <strong>{r.label}:</strong> {r.unavailableReason}
              </li>
            ))}
          </ul>
        </details>
      )}

      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.6rem 0 0' }}>
        Ranked against every eligible product in the catalog, not just these search results.
      </p>
    </section>
  );
}
